import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import { marked } from 'marked';

const MARKDOWN_PARSE_OPTIONS = { async: false as const };

/** Esquemas de href permitidos; o resto vira texto inerte. DOMPurify é a 2ª linha. */
const SAFE_HREF_PATTERN = /^(?:https?:\/\/|mailto:|\/|#)/i;

const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'button',
  'code',
  'del',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
];

const ALLOWED_ATTR = [
  'aria-label',
  'class',
  'colspan',
  'data-action',
  'data-code',
  'data-lang',
  'href',
  'rel',
  'rowspan',
  'target',
  'title',
  'type',
];

const HLJS_AUTO_SUBSET = ['json', 'typescript', 'bash', 'sql', 'yaml', 'xml'];

let initialized = false;

function ensureInitialized(): void {
  if (initialized) return;

  hljs.registerLanguage('bash', bash);
  hljs.registerLanguage('shell', bash);
  hljs.registerLanguage('sh', bash);
  hljs.registerLanguage('css', css);
  hljs.registerLanguage('javascript', javascript);
  hljs.registerLanguage('js', javascript);
  hljs.registerLanguage('json', json);
  hljs.registerLanguage('markdown', markdown);
  hljs.registerLanguage('md', markdown);
  hljs.registerLanguage('plaintext', plaintext);
  hljs.registerLanguage('text', plaintext);
  hljs.registerLanguage('python', python);
  hljs.registerLanguage('py', python);
  hljs.registerLanguage('sql', sql);
  hljs.registerLanguage('typescript', typescript);
  hljs.registerLanguage('ts', typescript);
  hljs.registerLanguage('html', xml);
  hljs.registerLanguage('xml', xml);
  hljs.registerLanguage('yaml', yaml);
  hljs.registerLanguage('yml', yaml);

  marked.use({
    breaks: true,
    gfm: true,
    renderer: {
      link({ href, title, tokens }) {
        const inner = this.parser.parseInline(tokens);
        if (!isSafeHref(href)) return inner;

        const isExternal = /^https?:\/\//i.test(href);
        const safeHref = escapeAttribute(href);
        const titleAttr = title ? ` title="${escapeAttribute(title)}"` : '';
        const className = isExternal ? 'iara-md__link iara-md__link--external' : 'iara-md__link';
        const externalAttrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';

        return `<a class="${className}" href="${safeHref}"${titleAttr}${externalAttrs}>${inner}</a>`;
      },
      code({ text, lang }) {
        const requested = (lang ?? '').trim().toLowerCase();
        const known = requested.length > 0 && Boolean(hljs.getLanguage(requested));

        let highlighted: string;
        let displayLang: string;

        if (known) {
          highlighted = hljs.highlight(text, { language: requested, ignoreIllegals: true }).value;
          displayLang = requested;
        } else if (requested.length > 0) {
          highlighted = escapeHtml(text);
          displayLang = requested;
        } else {
          const auto = hljs.highlightAuto(text, HLJS_AUTO_SUBSET);
          highlighted = auto.value;
          displayLang = auto.language ?? 'text';
        }

        const dataCode = escapeAttribute(text.replace(/\n+$/, ''));
        const langAttr = escapeAttribute(displayLang);

        return (
          `<div class="iara-code" data-code="${dataCode}" data-lang="${langAttr}">` +
          `<div class="iara-code__head">` +
          `<span class="iara-code__lang">${escapeHtml(displayLang)}</span>` +
          `<button type="button" class="iara-code__copy" data-action="copy-code" aria-label="Copiar código">Copiar</button>` +
          `</div>` +
          `<pre class="iara-code__pre palantir-scrollbar"><code class="language-${langAttr}">${highlighted}</code></pre>` +
          `</div>`
        );
      },
    },
  });

  initialized = true;
}

function isSafeHref(href: string): boolean {
  return !!href && SAFE_HREF_PATTERN.test(href.trim());
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Converte markdown GFM em HTML sanitizado, seguro para `[innerHTML]`.
 * - Blocos de código com label de linguagem + botão "Copiar" (fonte em `data-code`).
 * - Links externos forçam `target="_blank"` + `rel="noopener noreferrer"`; apenas
 *   esquemas seguros (http(s), mailto, âncoras, caminhos internos) são emitidos.
 */
export function markdownToSanitizedHtml(markdownSource: string): string {
  ensureInitialized();
  const raw = (markdownSource ?? '').trim();
  if (!raw) return '';

  const html = marked.parse(raw, MARKDOWN_PARSE_OPTIONS) as string;
  return DOMPurify.sanitize(html, { ALLOWED_ATTR, ALLOWED_TAGS });
}
